import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Send, Mail, Server, CheckCircle2, XCircle, KeyRound } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { useAuth } from "@/context/AuthContext";

const NEWSLETTERS = ["Daily Intelligence Brief", "Weekly Be-Apuo Briefing", "Global Crime Watch", "Ghana Intelligence", "Fact Check Alerts"];

function Stat({ label, value, testid }) {
  return (
    <div className="border border-zinc-800 rounded-sm p-3" data-testid={testid}>
      <div className="label-caps text-zinc-500 text-[0.6rem]">{label}</div>
      <div className="text-xl font-tactical font-black mt-1">{value}</div>
    </div>
  );
}

export default function EmailStudio() {
  const { user } = useAuth();
  const isSuperAdmin = Array.isArray(user?.roles) && user.roles.includes("super_admin");
  const [health, setHealth] = useState(null);
  const [dash, setDash] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(null); // {campaign, audience}
  const [form, setForm] = useState({ title: "", subject: "", body_html: "", newsletter: NEWSLETTERS[1] });
  const [testTo, setTestTo] = useState("");
  // Rotate manage-link (super admin only)
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateEmail, setRotateEmail] = useState("");
  const [rotateStage, setRotateStage] = useState("input"); // input | confirm | done
  const [rotating, setRotating] = useState(false);

  const load = () => {
    api.get("/admin/email/health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/admin/email/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/admin/email/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/email/campaigns", form); toast.success("Campaign created"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const askSend = async (c) => {
    try { const { data } = await api.get(`/admin/email/campaigns/${c.id}/audience`); setConfirm({ campaign: c, audience: data }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doSend = async () => {
    const id = confirm.campaign.id;
    setConfirm(null);
    try { const { data } = await api.post(`/admin/email/campaigns/${id}/send`); toast.success(`Sent: ${data.stats.sent}, skipped: ${data.stats.skipped}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const cancel = async (id) => { try { await api.post(`/admin/email/campaigns/${id}/cancel`); toast.success("Cancelled"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const showPreview = async (id) => { const { data } = await api.get(`/admin/email/campaigns/${id}/preview`); setPreview(data); };
  const sendTest = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/email/test", { to: testTo }); toast.success("Test email queued"); setTestOpen(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openRotate = () => { setRotateEmail(""); setRotateStage("input"); setRotating(false); setRotateOpen(true); };
  const closeRotate = () => { if (rotating) return; setRotateOpen(false); };
  const doRotate = async () => {
    if (rotating) return; // guard against duplicate clicks / duplicate emails
    setRotating(true);
    try {
      // Response intentionally carries NO token; we never read or display one.
      await api.post("/admin/email/newsletter/rotate-manage-link", { email: rotateEmail.trim() });
      setRotateStage("done");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setRotateStage("confirm");
    } finally {
      setRotating(false);
    }
  };

  const Badge = ({ ok, label }) => (
    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-zinc-800 rounded-sm">
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <XCircle className="h-3.5 w-3.5 text-amber-400" />}
      {label}
    </span>
  );

  return (
    <div className="p-6">
      <PageHeader title="Email Delivery" subtitle="Newsletter campaigns, delivery stats and provider status.">
        {isSuperAdmin && <Btn variant="ghost" data-testid="rotate-manage-link-btn" onClick={openRotate}><KeyRound className="h-4 w-4" /> Rotate Manage Link</Btn>}
        <Btn variant="ghost" data-testid="send-test-btn" onClick={() => setTestOpen(true)}><Send className="h-4 w-4" /> Send Test</Btn>
        <Btn data-testid="new-campaign-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Campaign</Btn>
      </PageHeader>

      {health && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="provider-health">
          <span className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4 text-zinc-500" /></span>
          <Badge ok={health.email.configured} label={`Email: ${health.email.provider}${health.email.mode === "mock" && health.email.provider !== "mock" ? " (mock)" : ""}`} />
          <span className="inline-flex items-center gap-1.5"><Server className="h-4 w-4 text-zinc-500" /></span>
          <Badge ok={health.storage.configured} label={`Storage: ${health.storage.provider}${health.storage.mode === "mock" && health.storage.provider !== "mock" ? " (mock)" : ""}`} />
        </div>
      )}

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="email-stats">
          <Stat testid="stat-active" label="Active" value={dash.subscribers.active} />
          <Stat testid="stat-pending" label="Pending" value={dash.subscribers.pending_confirmation} />
          <Stat testid="stat-unsub" label="Unsubscribed" value={dash.subscribers.unsubscribed} />
          <Stat testid="stat-suppressed" label="Suppressed" value={dash.subscribers.suppressed} />
          <Stat testid="stat-quarantined" label="Quarantined" value={dash.subscribers.quarantined} />
          <Stat testid="stat-sent" label="Sent" value={dash.messages.sent} />
          <Stat label="Delivered" value={dash.messages.delivered} />
          <Stat label="Bounced" value={dash.messages.bounced} />
          <Stat label="Complaints" value={dash.messages.complained} />
          <Stat label="Failed" value={dash.messages.failed} />
        </div>
      )}

      <Card>
        <table className="w-full text-sm" data-testid="campaigns-table">
          <thead><tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
            <th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">Newsletter</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Sent</th><th className="px-4 py-2.5 text-right">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-900">
            {campaigns.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-zinc-600">No campaigns yet.</td></tr>}
            {campaigns.map((c) => (
              <tr key={c.id} className="hover:bg-zinc-900/50" data-testid={`campaign-${c.id}`}>
                <td className="px-4 py-2.5">{c.title}</td>
                <td className="px-4 py-2.5 text-zinc-400">{c.newsletter}</td>
                <td className="px-4 py-2.5"><span className="uppercase text-xs font-mono-data">{c.status}</span></td>
                <td className="px-4 py-2.5">{c.stats?.sent ?? "—"}</td>
                <td className="px-4 py-2.5 text-right space-x-2">
                  <button className="text-xs text-zinc-400 hover:text-white" data-testid={`preview-${c.id}`} onClick={() => showPreview(c.id)}>Preview</button>
                  {["draft", "scheduled"].includes(c.status) && <button className="text-xs text-emerald-400 hover:text-emerald-300" data-testid={`send-${c.id}`} onClick={() => askSend(c)}>Send</button>}
                  {["draft", "scheduled"].includes(c.status) && <button className="text-xs text-amber-400 hover:text-amber-300" onClick={() => cancel(c.id)}>Cancel</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={rotateOpen} onClose={closeRotate} title="Rotate Manage Link">
        {rotateStage === "input" && (
          <form data-testid="rotate-input-form" onSubmit={(e) => { e.preventDefault(); if (rotateEmail.trim()) setRotateStage("confirm"); }} className="space-y-3">
            <p className="text-sm text-zinc-400">
              Revokes every existing manage-preferences link for this subscriber and emails them a fresh, secure link. Their subscriptions are not changed.
            </p>
            <Field label="Subscriber email">
              <input data-testid="rotate-email-input" type="email" required className={inputCls}
                value={rotateEmail} onChange={(e) => setRotateEmail(e.target.value)} />
            </Field>
            <Btn data-testid="rotate-continue-btn" type="submit" className="w-full justify-center">Continue</Btn>
          </form>
        )}
        {rotateStage === "confirm" && (
          <div className="space-y-4" data-testid="rotate-confirm">
            <div className="border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm px-3 py-2 rounded-sm">
              This will immediately revoke all existing manage links for:
            </div>
            <div className="font-mono-data text-sm break-all" data-testid="rotate-confirm-email">{rotateEmail.trim()}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="rotate-cancel-btn" disabled={rotating} onClick={() => setRotateStage("input")}>Back</Btn>
              <Btn data-testid="rotate-confirm-btn" disabled={rotating} onClick={doRotate} className="flex-1 justify-center">
                {rotating ? "Sending…" : "Confirm & Send"}
              </Btn>
            </div>
          </div>
        )}
        {rotateStage === "done" && (
          <div className="space-y-4" data-testid="rotate-done">
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <CheckCircle2 className="h-4 w-4" /> Old links revoked. A fresh manage-preferences email was sent.
            </div>
            <Btn data-testid="rotate-close-btn" onClick={() => setRotateOpen(false)} className="w-full justify-center">Close</Btn>
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New Campaign" wide>
        <form onSubmit={create} className="space-y-3">
          <Field label="Title"><input data-testid="campaign-title-input" className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Subject"><input data-testid="campaign-subject-input" className={inputCls} required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>
          <Field label="Newsletter"><select data-testid="campaign-newsletter-select" className={inputCls} value={form.newsletter} onChange={(e) => setForm({ ...form, newsletter: e.target.value })}>{NEWSLETTERS.map((n) => <option key={n}>{n}</option>)}</select></Field>
          <Field label="Body (HTML)"><textarea data-testid="campaign-body-input" className={inputCls} rows={6} required value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} /></Field>
          <Btn data-testid="campaign-save-btn" type="submit" className="w-full justify-center">Create Campaign</Btn>
        </form>
      </Modal>

      <Modal open={testOpen} onClose={() => setTestOpen(false)} title="Send Test Email">
        <form onSubmit={sendTest} className="space-y-3">
          <Field label="Recipient"><input data-testid="test-email-input" type="email" className={inputCls} required value={testTo} onChange={(e) => setTestTo(e.target.value)} /></Field>
          <Btn data-testid="test-send-btn" type="submit" className="w-full justify-center">Send Test</Btn>
        </form>
      </Modal>

      <Modal open={!!confirm} onClose={() => setConfirm(null)} title="Confirm Send">
        {confirm && (
          <div className="space-y-4" data-testid="send-confirm">
            <div className="border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm px-3 py-2 rounded-sm">
              This sends a real newsletter to all active, non-suppressed subscribers of "{confirm.audience.newsletter}". Pending and quarantined subscribers are excluded.
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="label-caps text-zinc-500">Newsletter</div>{confirm.audience.newsletter}</div>
              <div><div className="label-caps text-zinc-500">Recipients</div><span data-testid="confirm-recipient-count">{confirm.audience.recipient_count}</span></div>
              <div><div className="label-caps text-zinc-500">Suppressed (excluded)</div>{confirm.audience.suppressed_count}</div>
              <div><div className="label-caps text-zinc-500">Pending (excluded)</div>{confirm.audience.pending_count}</div>
            </div>
            {confirm.audience.over_limit ? (
              <div className="text-sm text-red-400" data-testid="over-limit-warning">
                Recipient count exceeds the safe synchronous limit ({confirm.audience.limit}). Queued delivery is required — send blocked.
              </div>
            ) : (
              <Btn data-testid="confirm-send-btn" onClick={doSend} className="w-full justify-center">Send to {confirm.audience.recipient_count} subscriber(s)</Btn>
            )}
          </div>
        )}
      </Modal>


      <Modal open={!!preview} onClose={() => setPreview(null)} title="Campaign Preview" wide>
        {preview && (
          <div data-testid="campaign-preview">
            <div className="text-sm text-zinc-400 mb-2">Subject: {preview.subject}</div>
            <iframe title="preview" srcDoc={preview.html} className="w-full h-[420px] bg-white rounded-sm" />
          </div>
        )}
      </Modal>
    </div>
  );
}
